<template>
  <div ref="modalElemRef" class="modal comment-modal" tabindex="-1" data-bs-backdrop="true" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Comments <span class="comment-count">({{ comments.length }})</span></h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <template v-if="comments.length">
            <div v-for="comment in comments" :key="comment.id" class="comment-item">
              <div class="comment-header">
                <img src="@/assets/icons/person_gray_150.png" class="comment-avatar"/>
                <span class="comment-author">{{ comment.authorName }}</span>
                <span class="comment-time">{{ comment.timestamp.toRelative() }}</span>
              </div>
              <div class="comment-text">{{ comment.text }}</div>
            </div>
          </template>
          <div v-else class="no-comments text-muted text-center">
            No comments yet
          </div>
        </div>
        <div class="modal-footer-input">
          <textarea
            class="form-control comment-input mb-2"
            v-model="newCommentText"
            placeholder="Add a comment..."
            rows="3"
          ></textarea>
          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!newCommentText.trim()"
              @click="submitComment"
            >Post</button>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<style lang="scss">
// Unscoped — Bootstrap creates the backdrop at document.body level, outside this component.
// z-index must beat Fancybox when teleported into its <dialog>.
.comment-modal {
  z-index: 1070 !important;
}
.comment-modal + .modal-backdrop {
  z-index: 1065 !important;
}
</style>

<style lang="scss" scoped>
.comment-count {
  color: #888;
  font-size: 0.875em;
}

.modal-body {
  padding: 0.5rem 0;
  max-height: 60vh;
  overflow-y: auto;
}

.comment-item {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--bs-border-color);

  &:last-child {
    border-bottom: none;
  }

  .comment-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.25rem;
  }

  .comment-avatar {
    height: 24px;
    width: 24px;
    border-radius: 50%;
    background-color: #ddd;
    padding: 3px;
    opacity: 60%;
  }

  .comment-author {
    font-weight: 600;
    font-size: 0.875rem;
  }

  .comment-time {
    font-size: 0.75rem;
    color: #888;
  }

  .comment-text {
    font-size: 0.875rem;
    white-space: pre-wrap;
    word-break: break-word;
    padding-left: calc(24px + 0.375rem);
  }
}

.no-comments {
  padding: 2rem 1rem;
  font-size: 0.875rem;
  color: #888;
}

.modal-footer-input {
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid var(--bs-border-color);
}

.comment-input {
  resize: none;
}

.btn-primary {
  min-width: 80px;
}
</style>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, onUnmounted } from 'vue'
import { Modal as BsModal } from 'bootstrap'
import { useCommentStore, type Comment } from '@/rd/CommentStore'

const emit = defineEmits<{
  commentAdded: []
}>()

defineExpose({ open, close })

const commentStore = useCommentStore()
const modalElemRef = ref<HTMLElement>()
const contentIdRef = ref('')
const newCommentText = ref('')

const comments = computed<Comment[]>(() => {
  if (!contentIdRef.value) {
    return []
  }
  return commentStore.getComments(contentIdRef.value)
})

onMounted(() => {
  modalElemRef.value?.addEventListener('hidden.bs.modal', onHidden)
})

onBeforeUnmount(() => {
  close()
})

onUnmounted(() => {
  modalElemRef.value?.removeEventListener('hidden.bs.modal', onHidden)
})

function onHidden() {
  contentIdRef.value = ''
  newCommentText.value = ''
}

function open(contentId: string) {
  contentIdRef.value = contentId
  newCommentText.value = ''
  BsModal.getOrCreateInstance(modalElemRef.value!).show()
}

function close() {
  BsModal.getInstance(modalElemRef.value!)?.hide()
}

function submitComment() {
  const text = newCommentText.value.trim()
  if (!text) {
    return
  }

  commentStore.addComment(contentIdRef.value, text)
  newCommentText.value = ''
  emit('commentAdded')
}
</script>
